<script setup>
import Card from "primevue/card";
import Button from 'primevue/button';
import DefaultLayout from '../../layouts/default.vue'
import FacilitiesTable from "@/components/facility/FacilitiesTable.vue";
import InputText from "primevue/inputtext";
import InputIcon from "primevue/inputicon";
import IconField from "primevue/iconfield";
import {computed, onMounted, ref} from "vue";
import {useToast} from "primevue";
import {useFacilityStore} from "@/stores/facility.js";
import FacilityForm from "@/components/facility/FacilityForm.vue";
import ActiveArchiveToggle from '@/components/common/ActiveArchiveToggle.vue';
import facilityApi from '@/api/facilityApi.js';
import { usePermissions } from '@/composables/usePermissions.js';
import { useRoute } from 'vue-router';


const search = ref('')
const checked = ref(true) // true = ACTIVE, false = ARCHIVED
const status = computed(() => (checked.value ? 'ACTIVE' : 'ARCHIVED'))
const showAddDialog = ref(false);
const facilityStore = useFacilityStore();
const toast = useToast();
const customerFilter = ref(undefined);
const activeCount = ref(null);
const archiveCount = ref(null);
const { hasPermission } = usePermissions();
const route = useRoute();
const responsible = computed(() => route.query.responsible ?? undefined);

function handleSave() {
  // FacilityForm сама сохраняет данные и показывает toast, родителю нужно только закрыть диалог
  showAddDialog.value = false;
}



async function fetchFacilityCounts({ q }) {
  const [activeResp, archiveResp] = await Promise.all([
    facilityApi.getFacilities({ page: 1, q, status: 'ACTIVE', responsible: responsible.value }),
    facilityApi.getFacilities({ page: 1, q, status: 'ARCHIVED', responsible: responsible.value }),
  ]);
  return {
    active: Number(activeResp?.count ?? 0),
    archive: Number(archiveResp?.count ?? 0),
  };
}

async function fetchArchiveCountOnly({ q }) {
  const archiveResp = await facilityApi.getFacilities({ page: 1, q, status: 'ARCHIVED', responsible: responsible.value });
  return Number(archiveResp?.count ?? 0);
}

onMounted(() => {
  try {
    const raw = sessionStorage.getItem('facilities:list:preset');
    if (raw) {
      const preset = JSON.parse(raw);
      if (preset && typeof preset === 'object') {
        if (preset.status === 'ACTIVE' || preset.status === 'ARCHIVED') {
          checked.value = preset.status === 'ACTIVE';
        }
        if (preset.customer_id != null && String(preset.customer_id).trim().length) {
          customerFilter.value = String(preset.customer_id);
        }
      }
    }
  } catch (_) {
    // ignore
  } finally {
    sessionStorage.removeItem('facilities:list:preset');
  }
});

function handleInitialCountLoaded({ count, status: loadedStatus }) {
  if (loadedStatus === 'ACTIVE') {
    activeCount.value = count;
  } else if (loadedStatus === 'ARCHIVED') {
    archiveCount.value = count;
  }
}

</script>
<template>
  <DefaultLayout>
    <Card class="w-full flex-1 flex flex-col min-h-0"
          :pt="{
            body: { class: 'flex-1 flex flex-col h-full' },
            content: { class: 'p-0 flex-1 flex flex-col min-h-0' }
          }"
    >
      <template #title>
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-8">
          <IconField>
            <InputIcon class="pi pi-search" />
            <InputText v-model="search" placeholder="Поиск объекта..."/>
          </IconField>

            <ActiveArchiveToggle
                v-if="hasPermission('facility.archive_facility')"
                v-model="checked"
                :search="search"
                :initial-active-count="activeCount"
                :initial-archive-count="archiveCount"
                :skip-initial-load="true"
                :fetchCounts="fetchFacilityCounts"
                :fetchArchiveCountOnly="fetchArchiveCountOnly"
                onIcon="pi pi-sun"
                offIcon="pi pi-moon"
                widthClass="w-36"
                ariaLabel="Фильтр по активности объектов"
            />
          </div>
            <Button
                v-if="hasPermission('facility.add_facility')"
                label="Добавить объект"
                icon="pi pi-plus"
                severity="info"
                rounded
                @click="showAddDialog = true"
            />
          </div>

          <FacilityForm v-model:visible="showAddDialog" @save="handleSave" />
      </template>
    <template #content>
      <FacilitiesTable
          :search="search"
          :status="status"
          :customer="customerFilter"
          :responsible="responsible"
          @initial-count-loaded="handleInitialCountLoaded"
      />
    </template>
  </Card>
  </DefaultLayout>
</template>

<style scoped>
</style>
